<script setup>
  import {ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {usePage} from "@inertiajs/vue3";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "MedicationComponentPharmaceuticalServices",
  });

  const props = defineProps({
    services: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();
  const pharmaceuticalServicesDialog = ref(null);
  const internalServices = ref(props.services);

  const openDialog = () => {
    pharmaceuticalServicesDialog.value.open();
  };

  watch(
    () => props.services,
    (newValue) => {
      internalServices.value = newValue;
    },
  );

  const famName = (keyFam) => {
    return page.props.medications?.data.find((medication) => medication.abdata_key_fam === keyFam).fam || null;
  };
</script>

<template>
  <div>
    <ComponentDialog
      ref="pharmaceuticalServicesDialog"
      dialog-type="information"
      dialog-title="Pharmazeutische Dienstleistungen (pDL)"
      dialog-width="medium"
      :test-id="getTestId('pharmaceutical-service')"
    >
      <template #content>
        <p>
          In der aktuellen Medikation sind Arzneimittel enthalten, die die Durchführung weiterer pharmazeutischer
          Dienstleistungen (nach VOASG) nahelegen.
        </p>

        <ul class="mt-4 space-y-4">
          <li v-if="internalServices.blood_pressure.has" class="space-y-2">
            <h3 class="text-lg font-medium">Standardisierte Risikoerfassung hoher Blutdruck</h3>
            <p>
              Patientinnen und Patienten erhalten mit dieser Dienstleistung ein Angebot, den Erfolg ihrer medikamentösen
              Blutdruckeinstellung standardisiert in der Apotheke kontrollieren zu lassen.
            </p>
            <p>Folgende Arzneimittel implizieren diese pDL:</p>

            <ul class="ml-6 list-disc">
              <li
                v-for="(keyFam, index) in internalServices.blood_pressure.keyFams"
                :key="index"
                v-text="famName(keyFam)"
              />
            </ul>

            <div class="flex items-center">
              <MedLink
                variant="primary"
                icon="launch"
                href="https://www.abda.de/pharmazeutische-dienstleistungen/standardisierte-risikoerfassung-hoher-blutdruck/"
                target="_blank"
                :data-test="getTestId('pdl-blood-pressure-abda-link')"
              >
                Informationsseite der ABDA
              </MedLink>
            </div>
          </li>

          <li v-if="internalServices.inhaler.has" class="space-y-2">
            <h3 class="text-lg font-medium">
              Erweiterte Einweisung in die korrekte Arzneimittelanwendung mit Üben der Inhalationstechnik
            </h3>
            <p>
              Durch diese Dienstleistung erhalten Patientinnen und Patienten ein Angebot, ihre Inhalationstechnik nach
              einem standardisierten Prozess qualitätsgesichert zu üben.
            </p>
            <p>Folgende Arzneimittel implizieren diese pDL:</p>
            <ul class="ml-6 list-disc">
              <li v-for="(keyFam, index) in internalServices.inhaler.keyFams" :key="index" v-text="famName(keyFam)" />
            </ul>

            <div class="flex items-center">
              <MedLink
                variant="primary"
                icon="launch"
                href="https://www.abda.de/pharmazeutische-dienstleistungen/inhalationstechnik/"
                target="_blank"
                :data-test="getTestId('pdl-inhaler-abda-link')"
              >
                Informationsseite der ABDA
              </MedLink>
            </div>
          </li>

          <li v-if="internalServices.oral_antitumour_therapy.has" class="space-y-2">
            <h3 class="text-lg font-medium">Pharmazeutische Betreuung bei oraler Antitumortherapie</h3>
            <p>
              Patientinnen und Patienten, die ein orales Antitumortherapeutikum neu verordnet bekommen, haben
              <strong>im ersten halben Jahr nach Beginn der ambulanten Therapie</strong>
              einen Anspruch auf eine „Erweiterte Medikationsberatung“ in der Apotheke. Im persönlichen Gespräch werden
              neben den bei dieser Leistung genannten Aspekten zudem die Besonderheiten der oralen Antitumortherapie
              adressiert. Bei Bedarf erfolgt zwei bis sechs Monate nach der „Erweiterten Medikationsberatung“ in der
              Apotheke ein Folgegespräch, um potenzielle Anwendungsprobleme, Probleme bei der Therapietreue oder
              Nebenwirkungen zu besprechen.
            </p>
            <p>Folgende Arzneimittel implizieren diese pDL:</p>
            <ul class="ml-6 list-disc">
              <li
                v-for="(keyFam, index) in internalServices.oral_antitumour_therapy.keyFams"
                :key="index"
                v-text="famName(keyFam)"
              />
            </ul>

            <div class="flex items-center">
              <MedLink
                variant="primary"
                icon="launch"
                href="https://www.abda.de/pharmazeutische-dienstleistungen/orale-krebstherapie/"
                target="_blank"
                :data-test="getTestId('pdl-oral-antitumour-abda-link')"
              >
                Informationsseite der ABDA
              </MedLink>
            </div>

            <!-- prettier-ignore -->
            <p>
              Für Ihre Beratung können Sie die Arzneimittelmerkblätter von AMBORA
              („<strong>A</strong>rznei<strong>M</strong>itteltherapiesicherheit bei der <strong>B</strong>ehandlung mit <strong>OR</strong>alen <strong>A</strong>ntitumor-Wirkstoffen“)
              verwenden.
            </p>

            <MedLink
              variant="primary"
              icon="launch"
              href="https://www.ambora.org"
              target="_blank"
              :data-test="getTestId('pdl-ambora-link')"
            >
              www.ambora.org
            </MedLink>
          </li>
        </ul>
      </template>
    </ComponentDialog>

    <template
      v-if="
        internalServices?.blood_pressure?.has ||
        internalServices?.inhaler?.has ||
        internalServices?.oral_antitumour_therapy?.has
      "
    >
      <MedButton
        variant="primary"
        flat
        icon="medical_services"
        :data-test="getTestId('pharmaceutical-service-open-dialog-button')"
        @click="openDialog()"
      >
        weitere pDL
      </MedButton>
    </template>
  </div>
</template>
